import { catchError } from "@opendash/core";
import { Config } from "@opendash/plugin-parse/dist/types-generated";
import Parse from "parse";
import { useCallback, useEffect, useState } from "react";
import { OpenService } from "../../../types/config";

export const useConfig = <T,>(key: OpenService.ConfigKeys) => {
  const [value, setValue] = useState<T | undefined>(undefined);
  const [doReload, setDoReload] = useState(0);

  const getConfig = useCallback(async () => {
    const [configError, config] = await catchError(
      new Parse.Query(Config).equalTo("key", key).descending("priority").first()
    );

    if (configError) {
      console.warn(configError);
    }

    if (config) {
      setValue(JSON.parse(config.get("value")) as T);
    }
  }, [key]);

  //Load config on first render
  useEffect(() => {
    getConfig();
  }, [key, doReload]);

  async function saveConfig(valueToSave: string): Promise<boolean> {
    //Check if config already exists. If it does, update it. If it doesn't, create a new one.
    //For checking if config already exists, we can use value from useState. It is undefined if config doesn't exist.
    if (value === undefined) {
      //Config doesn't exist. Create a new one.
      const newConfig = new Config();
      newConfig.set("key", key);
      newConfig.set("value", valueToSave);
      newConfig.set("priority", 0);
      const savedconfig = await newConfig.save();
      setValue(JSON.parse(savedconfig.get("value")) as T);
      return true;
    } else {
      //Config exists. Update it.
      const [configError, config] = await catchError(
        new Parse.Query(Config).equalTo("key", key).first()
      );

      if (configError) {
        console.warn(configError);
        return false;
      }

      if (config) {
        config.set("value", valueToSave);
        const savedconfig = await config.save();
        setValue(JSON.parse(savedconfig.get("value")) as T);
      }

      return true;
    }
  }

  return { value, saveConfig, reload: () => setDoReload((prev) => prev + 1) };
};
